import { type ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Empty } from "./ui";

export interface Column<T> {
  key: string;
  label: ReactNode;
  render: (row: T) => ReactNode;
  sort?: (row: T) => string | number | null | undefined;
  align?: "right";
  className?: string;
  hideSm?: boolean;
}

/** Sortable, clickable data table. Rows link to `href(row)` when given. */
export function DataTable<T>({
  rows,
  columns,
  href,
  empty,
  rowClass,
  limit = 100,
  onRowClick,
}: {
  rows: T[];
  columns: Column<T>[];
  href?: (row: T) => string | null;
  empty?: ReactNode;
  rowClass?: (row: T) => string;
  limit?: number;
  onRowClick?: (row: T) => void;
}) {
  const nav = useNavigate();
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [shown, setShown] = useState(limit);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sort) return rows;
    return [...rows].sort((a, b) => {
      const x = col.sort!(a),
        y = col.sort!(b);
      if (x == null) return 1;
      if (y == null) return -1;
      return (
        (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) *
        sort.dir
      );
    });
  }, [rows, sort, columns]);

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${c.align === "right" ? "r" : ""} ${c.hideSm ? "hide-sm" : ""}`}
                data-sort={c.sort ? c.key : undefined}
                data-dir={sort?.key === c.key ? (sort.dir === 1 ? "asc" : "desc") : undefined}
                onClick={
                  c.sort
                    ? () => setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === 1 ? -1 : 1 }))
                    : undefined
                }
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length}>{empty ?? <Empty title="Nothing here yet" />}</td>
            </tr>
          )}
          {sorted.slice(0, shown).map((row, i) => {
            const to = href?.(row);
            return (
              <tr
                key={i}
                data-href={to || onRowClick ? "1" : undefined}
                className={rowClass?.(row)}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("a,button,input,select")) return;
                  if (onRowClick) onRowClick(row);
                  else if (to) nav(to);
                }}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`${c.align === "right" ? "r num" : ""} ${c.className ?? ""} ${c.hideSm ? "hide-sm" : ""}`}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {sorted.length > shown && (
        <div className="table-foot">
          <button type="button" className="btn btn-ghost" onClick={() => setShown((s) => s + limit)}>
            Show {Math.min(limit, sorted.length - shown)} more of {sorted.length - shown}
          </button>
        </div>
      )}
    </>
  );
}
